// Global imports.
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Stylesheets and media.
import styles from './Button.module.css';

// Utils, actions, sagas, etc.
import { getAsset } from '../../utils/utils';
import { BUTTON_VARIANT } from '../../utils/constants';

/* eslint-disable react/button-has-type */

const cx = classNames.bind(styles);

const Button = ({
    className,
    variant,
    icon,
    customIcon,
    smallIcon,
    value,
    title,
    type,
    withWrap,
    disabled,
    showLoader,
    showIndicator,
    indicatorValue,
    testName,
    onClick,
    menuButton,
    mobileMenuButton,
    mobileMenuItem,
    reactivateButton,
}) => {

    const getButtonClass = () => {
        if (variant === BUTTON_VARIANT.OUTLINE_BLUE) {
            return 'buttonOutlineBlue';
        }
        if (variant === BUTTON_VARIANT.OUTLINE_BEIGE) {
            return 'buttonOutlineBeige';
        }

        return !!withWrap ? 'buttonWithWrap' : 'button';
    };

    const buttonClasses = cx(getButtonClass(), className);
    const classes = classNames(
        { [styles.menuButton]: !!menuButton },
        { [styles.mobileMenuButton]: !!mobileMenuButton },
        { [styles.mobileMenuItem]: !!mobileMenuItem },
        styles.flexContainer,
    );
    const iconClasses = classNames(
        { [styles.smallIcon]: !!smallIcon },
        { [styles.customIcon]: !!customIcon },
        styles.icon,
    );

    return (
        <button
            data-test={testName}
            className={buttonClasses}
            type={type}
            title={title}
            disabled={disabled}
            onClick={onClick}
        >
            <div className={classes}>
                {!!icon && !showLoader && !mobileMenuItem && !reactivateButton && !customIcon
                    && <FontAwesomeIcon icon={icon} className={iconClasses} size={`${smallIcon ? 1 : 2}x`} />}
                {!!customIcon && !icon && !showLoader && !mobileMenuItem && !reactivateButton
                    && <img src={getAsset(customIcon)} alt="icon" className={iconClasses} />}
                {!!showLoader && <FontAwesomeIcon className={styles.spinner} icon="spinner" spin />}
                <div className={styles.valueWrapper}>{value}</div>
                {!!showIndicator && <div className={styles.indicator}>{indicatorValue}</div>}
            </div>
        </button>
    );
};

Button.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    customIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    smallIcon: PropTypes.bool,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    title: PropTypes.string,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    withWrap: PropTypes.bool,
    disabled: PropTypes.bool,
    showLoader: PropTypes.bool,
    showIndicator: PropTypes.bool,
    indicatorValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    testName: PropTypes.string,
    onClick: PropTypes.func,
    menuButton: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    mobileMenuButton: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    mobileMenuItem: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    reactivateButton: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

Button.defaultProps = {
    className: '',
    variant: '',
    icon: '',
    customIcon: '',
    smallIcon: false,
    value: '',
    title: '',
    type: 'button',
    withWrap: false,
    disabled: false,
    showLoader: false,
    showIndicator: false,
    indicatorValue: '',
    testName: '',
    onClick: () => {},
    menuButton: '',
    mobileMenuButton: false,
    mobileMenuItem: '',
    reactivateButton: '',
};

export default Button;
